<template>
  <section>
    <mobile-cart v-if="standalone" class="mobile-cart"></mobile-cart>
    <section id="cart">
      <order-file-uploader
        class="file-list"
        :drop-target="dropTarget"
        @file-progress="onFileProgress"
        @drag-over="onDragOver"
        @drag-leave="dragGlow = false"
        @upload-done="onUploadDone"
        @files-added="onFilesAdded"
        @file-removed="onFileRemoved"
      >
        <file-list
          @select-all="selectAll"
          @select-file="selectFile"
          @remove="clearFile"
          @add-to-cart="AddToCartByFile"
          :class="{ 'drag-glow': dragGlow }"
          :selected-files="selectedFiles"
          :uploadProgress="rootStore.uploadProgress"
          :inProgress="rootStore.uploadInProgress"
          :standalone="standalone"
        ></file-list>
      </order-file-uploader>
      <section id="file-specs" class="card">
        <product-list
          v-if="view === 'productList'"
          :secure="secure"
          :selected-files="selectedFiles"
          :files-in-list="fileListLength"
          :selected-product-title="selectedProductTitle"
          @select-product="selectProduct"
          @select-user-template="selectUserTemplate"
        ></product-list>
        <product-specs
          v-else-if="view === 'productSpecs'"
          :product="selectedProduct"
          :selected-files="selectedFiles"
          :standalone="standalone"
          :single-product="availableProducts.length === 1"
          @add-to-cart="addToCart"
          @show-products="view = 'productList'"
        ></product-specs>
        <user-template-specs
          v-else-if="view === 'userTemplate'"
          :product="selectedFiles[0].productSpecs"
          @add-to-cart="addToCart"
          @show-products="view = 'productList'"
          :standalone="standalone"
          :selected-files="selectedFiles"
        ></user-template-specs>
      </section>
    </section>
  </section>
</template>

<script>
import OrderMixin from '@/mixins/OrderMixin'
import DialogMixin from '@/mixins/DialogMixin'

import { mapStores } from 'pinia'
import { useProductOrderStore } from '@/features/productOrder/ProductOrder.store'
import { useCheckOutStore } from '@/features/checkOut/CheckOut.store'

import ProductSpecs from './children/ProductSpecs.vue'
import FileList from './children/FileList.vue'
import ProductList from './children/ProductList.vue'
import UserTemplateSpecs from './children/UserTemplateSpecs.vue'
import ProductOrderMixin from './ProductOrderMixin'
import OrderFileUploader from '@/components/ui/file-upload/OrderFileUploader.vue'
import MobileCart from './children/MobileCart.vue'

import AllProductsInCart from './children/AllProductsInCart.vue'

import { useFileListStore } from '@/stores/FileList.store'

import { format } from 'date-fns'

export default {
  mixins: [OrderMixin, ProductOrderMixin, DialogMixin],
  components: {
    FileList,
    ProductList,
    ProductSpecs,
    UserTemplateSpecs,
    OrderFileUploader,
    MobileCart
  },
  props: {
    secure: {
      type: Boolean,
      default: false
    },
    toSelect: {
      type: Object,
      default: function () {
        return {}
      }
    },
    standalone: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      errors: [],
      selectedFiles: [],
      selectedProduct: {},
      dragGlow: false,
      //progress: 0,
      //inProgress: false,
      view: 'productList'
    }
  },
  created() {
    if (this.toSelect.fileName) {
      const toSelect = this.fileListStore.fileList.find((file) => {
        return file.fileName === this.toSelect.fileName
      })
      this.selectFile(toSelect, 'single')
    } else if (
      this.fileListStore.fileList.length > 0 &&
      this.firstSelectableFile
    ) {
      this.selectFile(this.firstSelectableFile, 'single')
    }

    this.productOrderStore.hydrateUserPrintTemplates()

    if (this.availableProducts.length === 1) {
      const product = this.availableProducts[0]
      const isSelectable = (file) =>
        !file.inCart &&
        file.uploadFileType !== 'ADDRESS_LIST' &&
        file.uploadFileType !== 'ATTACHMENT_FILE'
      const hasEmptySpecs = (file) =>
        !file.productSpecs?.title ||
        (file.productSpecs.specs && file.productSpecs.specs.length === 0) ||
        Object.keys(file.productSpecs).length === 0

      const filesToAssign = this.fileListStore.fileList.filter(
        (file) => isSelectable(file) && hasEmptySpecs(file)
      )

      if (
        filesToAssign.length > 0 &&
        this.productFileTypeCheck(product, filesToAssign)
      ) {
        const firstFileName = filesToAssign[0].fileName
        this._afterSelectProduct = () => {
          this.selectedFiles = [
            this.fileListStore.fileList.find((f) => f.fileName === firstFileName) || filesToAssign[0]
          ]
        }
        this.selectedFiles = filesToAssign
        this.selectProduct(product)
      }
    }
  },
  methods: {
    onDragOver() {
      this.dragGlow = true
    },
    onFilesAdded() {
      this.dragGlow = false
      this.rootStore.uploadInProgress = true
    },
    onUploadDone() {
      this.dragGlow = false
      this.rootStore.uploadInProgress = false
      this.rootStore.uploadProgress = 0
      /*  if (this.selectedFiles.length === 0) {
        this.selectFile(this.fileListStore.fileList[0], 'single')
      } */
    },
    onFileRemoved() {
      this.rootStore.uploadInProgress = false
      this.rootStore.uploadProgress = 0
    },
    onFileProgress(value) {
      this.dragGlow = false

      // working around a bug in component here - it passes the wrong value at 100%
      if (!isNaN(value)) {
        this.rootStore.uploadProgress = value
      }
    },
    showCartPopup() {
      this.$modal.show(
        AllProductsInCart,
        {},
        {
          height: 'auto',
          width: 420,
          adaptive: true
        },
        {}
      )
    },
    addToCart() {
      const missingFile = this.selectedFiles.find((f) => this.fileHasRequiredSpecsMissing(f))
      if (missingFile) {
        this.notificationDialog(
          'Verplicht veld ontbreekt',
          '<p>Vul alle verplichte velden in voordat u het bestand aan de winkelwagen toevoegt.</p>'
        )
        return
      }

      setTimeout(() => {
        this.fileListStore.updateFileSpec(
          this.selectedFileNames,
          'inCart',
          true
        )
        this.checkOutStore.addProduct(this.selectedFiles)

        this.selectedProduct = {}
        this.selectedFiles = []
        this.view = 'productList'

        if (this.fileListLength === 0) {
          this.showCartPopup()
        }
      }, 1000)
    },
    AddToCartByFile(file) {
      if (
        this.selectedFiles.findIndex((fl) => fl.fileName === file.fileName) > -1
      ) {
        this.selectedFiles.splice(
          this.selectedFiles.findIndex((fl) => fl.fileName === file.fileName),
          1
        )

        if (this.selectedFiles.length === 0) {
          this.view = 'productList'
          this.selectedProduct = {}
        }
      }

      this.fileListStore.updateFileSpec([file.fileName], 'inCart', true)
      this.checkOutStore.addProduct([file])

      if (this.fileListLength === 0) {
        this.showCartPopup()
      }
    },
    selectFile(file, type) {
      if (type === 'single') {
        this.selectedFiles = []
      }

      if (!this.selectedFiles.some((fl) => fl.fileName === file.fileName)) {
        this.selectedFiles.push(file)
        this.view = 'productList'
      } else {
        this.selectedFiles.splice(
          this.selectedFiles.findIndex((fl) => fl.fileName === file.fileName),
          1
        )
      }

      if (this.selectedFiles.length == 0) {
        this.view = 'productList'
        return
      }

      // Use the first selected file to check product/template (after selection change)
      const firstSelectedFile = this.selectedFiles[0]

      // Check if all selected files have the same userTemplate
      if (
        this.sameProductSelected &&
        firstSelectedFile.productSpecs.userTemplate
      ) {
        this.view = 'userTemplate'
        return
      }

      // Check if all selected files have the same product
      if (this.sameProductSelected && firstSelectedFile.productSpecs.title) {
        const product = this.productOrderStore.products.find(
          (prod) => prod.title === firstSelectedFile.productSpecs.title
        )
        if (product) {
          this.selectProduct(product)
          return
        }
      }

      // Auto-select if only one product is available
      if (
        !firstSelectedFile.productSpecs.title &&
        this.autoSelectableProducts.length === 1 &&
        this.productSecurityCheck(
          this.autoSelectableProducts[0],
          this.selectedFiles
        )
      ) {
        this.selectProduct(this.autoSelectableProducts[0])
      }
    },
    selectAll() {
      const selectableFiles = this.fileListStore.fileList.filter(
        (file) =>
          !file.inCart &&
          file.uploadFileType != 'ADDRESS_LIST' &&
          file.uploadFileType != 'ATTACHMENT_FILE'
      )
      if (
        this.selectedFiles.length > 0 &&
        this.selectedFiles.length === selectableFiles.length
      ) {
        this.selectedFiles = []
      } else {
        this.selectedFiles = [...selectableFiles]
      }
      this.view = 'productList'
    },
    async clearFile(file) {
      // If file is not selected delete only this one
      // If file is selected delete all selected
      const fileIsSelected = this.selectedFiles.find(
        (selectedFile) => selectedFile.uuid === file.uuid
      )
      this.checkOutStore.removeFile(file.uuid)

      if (!fileIsSelected) {
        await this.fileListStore.removeFile(file.uuid)
      } else {
        let idsToRemove = this.selectedFiles.map((f) => f.uuid)
        await this.fileListStore.removeFiles(idsToRemove)
        this.selectedFiles = []
      }

      this.view = 'productList'
    },
    selectUserTemplate(template) {
      let productSpecs = template.template.productSpecs.specs
        ? template.template.productSpecs.specs
        : template.template.productSpecs

      // Als productSpecs een leeg object is, kopieer de specs van template.template
      if (Object.keys(productSpecs).length === 0) {
        productSpecs = {
          colorMode: template.template.colorMode || '',
          format: template.template.format || '',
          material: template.template.material || '',
          side: template.template.side || '',
          treatment: template.template.treatment || ''
        }
      }

      // Strip Vue reactivity proxy to get a plain value (prevents _custom wrapper in store)
      productSpecs = JSON.parse(JSON.stringify(productSpecs))

      this.fileListStore.addProductOrderProduct([
        this.selectedFileNames,
        {
          title: template.template.name,
          userTemplate: true,
          secured: template.template.securityLevel > 0 ? true : false,
          specs: productSpecs
        }
      ])

      this.view = 'userTemplate'
    },
    selectProduct(product) {
      this.selectedProduct = product

      if (
        !this.selectedFiles[0].productSpecs.specs ||
        this.selectedFiles[0].productSpecs.title != product.title ||
        !this.sameProductSelected
      ) {
        this.fileListStore.addProductOrderProduct([
          this.selectedFileNames,
          {
            title: product.title,
            priceType: product.priceType || '',
            secured: product.securityLevel > 0 ? true : false,
            alwaysColor: product.alwaysColor,
            hidePrice: product.hidePrice,
            specs: []
          },
          product.divisionCode ?? null
        ])

        // Read the file from the store: when the analysis finishes,
        // updatePrintSpecs() replaces the object in fileList, leaving a stale
        // reference in selectedFiles with numberOfLines still at 0
        const liveFile =
          this.fileListStore.fileList.find(
            (f) => f.fileName === this.selectedFiles[0].fileName
          ) || this.selectedFiles[0]

        let quantity = this.productQuantity(product, liveFile)

        this.fileListStore.updateProductOrderQuantity([
          this.selectedFileNames,
          quantity
        ])

        let defSpecs = []
        product.menus.forEach((menu) => {
          // Check if this menu has a toggleOptions that defaults to disabled
          const toggleGroup = menu.optionGroups.find(
            (og) => og.name === 'toggleOptions'
          )
          const menuDisabledByToggle =
            toggleGroup && toggleGroup.default === 'disabled'

          menu.optionGroups.forEach((optionGroup) => {
            // Skip non-toggle optionGroups when menu is disabled by toggle
            if (menuDisabledByToggle && optionGroup.name !== 'toggleOptions') {
              return
            }
            let defaultOption
            let valueSet = false

            if (optionGroup.name === 'opmerking') {
              defaultOption = {
                name: '',
                title: '',
                priceKey: 'opmerking'
              }
              valueSet = true
            } else if (optionGroup.name === 'datePicker') {
              const today = new Date()
              let daysToAdd = 4
              let resultDate = new Date(today)

              while (daysToAdd > 0) {
                resultDate.setDate(resultDate.getDate() + 1)
                const dayOfWeek = resultDate.getDay()
                // Skip Monday (1) and Sunday (0)
                if (dayOfWeek !== 0 && dayOfWeek !== 1) {
                  daysToAdd--
                }
              }

              const titlePrefix = optionGroup.options[0].title

              defaultOption = {
                name: resultDate,
                title:
                  titlePrefix +
                  ': ' +
                  format(new Date(resultDate), 'dd-MM-yyyy'),
                priceKey: 'datePicker'
              }
              valueSet = true
            } else if (optionGroup.name === 'buttons') {
              defaultOption = {
                name: '',
                title: '',
                priceKey: 'buttons'
              }
              valueSet = true
            } else if (optionGroup.analyseValue) {
              const values = []
              optionGroup.options.forEach((option) => {
                if (option.subOptions && option.subOptions.length) {
                  option.subOptions.forEach((subO) => {
                    values.push(subO.name)
                  })
                } else {
                  values.push(option.name)
                }
              })

              if (
                optionGroup.name === 'formaat' &&
                values.includes(this.selectedFiles[0].format) &&
                this.selectedFiles[0].format != 'Als bestand'
              ) {
                defaultOption = {
                  name: this.selectedFiles[0].format,
                  title: this.selectedFiles[0].format,
                  priceKey: 'formaat'
                }
                valueSet = true
              }
            }

            const pagesTotal =
              this.selectedFiles[0].pagesColour + this.selectedFiles[0].pagesBW
            if (optionGroup.name === 'printwijze' && pagesTotal === 1) {
              defaultOption = {
                name: 'Enkelzijdig',
                title: 'Enkelzijdig',
                priceKey: 'printwijze'
              }
              valueSet = true
            }

            if (valueSet === false) {
              const currentOptionNames = defSpecs.map((s) => s.optionName)
              const isHiddenByDefSpecs = (opt) => {
                if (!opt.hideWhen?.length) return false
                return opt.hideWhen.some((cond) => currentOptionNames.includes(cond))
              }
              const isParentHidden = (opt) => {
                for (const parentOpt of optionGroup.options) {
                  if (
                    parentOpt.subOptions?.some((sub) => sub.name === opt.name) &&
                    parentOpt.hideWhen?.some((cond) => currentOptionNames.includes(cond))
                  ) {
                    return true
                  }
                }
                return false
              }
              const isOptHidden = (opt) => isHiddenByDefSpecs(opt) || isParentHidden(opt)

              optionGroup.options.forEach((option) => {
                if (option.name === optionGroup.default) {
                  // Check if option meets minPagesRequired and is not hidden
                  if (
                    (option.minPagesRequired && option.minPagesRequired > pagesTotal) ||
                    isOptHidden(option)
                  ) {
                    // Skip this default, don't set it
                    return
                  }
                  defaultOption = option
                } else if (option.subOptions && option.subOptions.length > 0) {
                  option.subOptions.forEach((subO) => {
                    if (subO.name === optionGroup.default) {
                      // Check if subOption meets minPagesRequired and is not hidden
                      if (
                        (subO.minPagesRequired && subO.minPagesRequired > pagesTotal) ||
                        isOptHidden(subO)
                      ) {
                        return
                      }
                      defaultOption = subO
                    }
                  })
                }
              })

              // If default was skipped, find first available non-hidden option
              if (!defaultOption) {
                for (const option of optionGroup.options) {
                  if (
                    (!option.minPagesRequired || option.minPagesRequired <= pagesTotal) &&
                    !isOptHidden(option)
                  ) {
                    if (option.subOptions && option.subOptions.length > 0) {
                      const firstSub = option.subOptions.find(
                        (subO) =>
                          (!subO.minPagesRequired || subO.minPagesRequired <= pagesTotal) &&
                          !isOptHidden(subO)
                      )
                      if (firstSub) {
                        defaultOption = firstSub
                        break
                      }
                    } else {
                      defaultOption = option
                      break
                    }
                  } else if (option.subOptions && option.subOptions.length > 0) {
                    const firstSub = option.subOptions.find(
                      (subO) =>
                        (!subO.minPagesRequired || subO.minPagesRequired <= pagesTotal) &&
                        !isOptHidden(subO)
                    )
                    if (firstSub) {
                      defaultOption = firstSub
                      break
                    }
                  }
                }
              }
            }

            // If the default option is a parent with subOptions (no priceKey), use the first subOption
            if (defaultOption?.subOptions?.length > 0) {
              defaultOption = defaultOption.subOptions[0]
            }

            const defaultOptionObj = {
              title: menu.title || '',
              optionGroupName: optionGroup.name || '',
              optionName: defaultOption.name || '',
              optionTitle: defaultOption.title || '',
              productionTitle:
                defaultOption.productionTitle || defaultOption.title || '',
              priceKey: defaultOption.priceKey,
              priceValue: defaultOption.priceValue,
              productionCategory: optionGroup.productionCategory || menu.title
            }

            // Add priceObjectKey if the optionGroup has it
            if (optionGroup?.priceObjectKey) {
              defaultOptionObj.priceObjectKey = optionGroup.priceObjectKey
            }

            if (menu.hidden) {
              defaultOptionObj.hidden = true
            }

            // Add fileTypeRequired if the menu has it
            if (menu.fileTypeRequired) {
              defaultOptionObj.fileTypeRequired = menu.fileTypeRequired
            }

            defSpecs.push(defaultOptionObj)
          })
        })

        // Remove specs from menus that should be hidden based on default selections
        const hiddenMenuTitles = product.menus
          .filter((menu) => {
            if (!menu.hideWhen || !menu.hideWhen.length) return false
            return menu.hideWhen.some((condition) =>
              defSpecs.some((spec) => spec.optionName === condition)
            )
          })
          .map((menu) => menu.title)

        if (hiddenMenuTitles.length) {
          defSpecs = defSpecs.filter(
            (spec) => !hiddenMenuTitles.includes(spec.title)
          )
        }

        // Remove specs from menus that require a file type that is not yet present
        defSpecs = defSpecs.filter((spec) => {
          if (!spec.fileTypeRequired) return true
          return this.selectedFiles[0].productSpecs.files?.some(
            (f) => f.type === spec.fileTypeRequired
          )
        })

        this.fileListStore
          .productOrderSpecUpdate({
            fileNames: this.selectedFileNames,
            spec: defSpecs,
            hydrate: true
          })
          .then(() => {
            // Refresh selectedFiles with updated references from store
            // This is necessary because Pinia updates the store objects but
            // selectedFiles still references the old objects before the update
            this.selectedFiles = this.selectedFileNames
              .map((fileName) => {
                return this.fileListStore.fileList.find(
                  (f) => f.fileName === fileName
                )
              })
              .filter((f) => f !== undefined)

            this.view = 'productSpecs'

            if (this._afterSelectProduct) {
              this._afterSelectProduct()
              this._afterSelectProduct = null
            }
          })
      } else {
        // Specs already exist, safe to show immediately
        this.view = 'productSpecs'

        if (this._afterSelectProduct) {
          this._afterSelectProduct()
          this._afterSelectProduct = null
        }
      }
    }
  },
  computed: {
    ...mapStores(useProductOrderStore, useFileListStore, useCheckOutStore),
    firstSelectableFile() {
      const isSelectable = (file) =>
        !file.inCart &&
        file.uploadFileType !== 'ADDRESS_LIST' &&
        file.uploadFileType !== 'ATTACHMENT_FILE'

      const hasEmptySpecs = (file) =>
        !file.productSpecs ||
        !file.productSpecs.title ||
        (file.productSpecs.specs && file.productSpecs.specs.length === 0) ||
        Object.keys(file.productSpecs).length === 0

      // Prefer files with empty specs, otherwise any selectable file
      return (
        this.fileListStore.fileList.find(
          (file) => isSelectable(file) && hasEmptySpecs(file)
        ) || this.fileListStore.fileList.find(isSelectable)
      )
    },
    selectedFileNames() {
      let fileNames = []
      this.selectedFiles.forEach((file) => {
        fileNames.push(file.fileName)
      })
      return fileNames
    },
    productsComplete() {
      return (
        this.fileListStore.fileList.length &&
        this.fileListStore.fileList.every((prod) => {
          return Object.prototype.hasOwnProperty.call(prod.productSpecs, 'title')
        })
      )
    },
    selectedProductTitle() {
      if (
        this.sameProductSelected &&
        !this.selectedFiles[0].productSpecs.userTemplate
      ) {
        return this.selectedFiles[0].productSpecs.title
      } else {
        return ''
      }
    },
    sameProductSelected() {
      if (this.selectedFiles.length === 1) {
        return true
      } else if (this.selectedFiles.length === 0) {
        return false
      }

      // Check if all files have the same productSpecs.title first
      const firstTitle = this.selectedFiles[0].productSpecs.title
      for (let i = 1; i < this.selectedFiles.length; i++) {
        if (this.selectedFiles[i].productSpecs.title !== firstTitle) {
          return false
        }
      }

      // Only compare full specs if titles match
      const specsToCompare = []
      this.selectedFiles.forEach((file) => {
        let specs = JSON.stringify(file.productSpecs.specs)
        let specsAndQuantity = specs + file.quantity
        specsToCompare.push(specsAndQuantity)
      })
      for (let i = 0; i < specsToCompare.length; i++) {
        if (specsToCompare[0] !== specsToCompare[i]) {
          return false
        }
      }

      return true
    },
    // Quantity that each file with an analysis-derived product should have, based on
    // the current analysis results. Watched so the quantity catches up when the
    // analysis finishes after the product was already selected.
    analysedQuantities() {
      return this.fileListStore.fileList
        .filter((file) => !file.inCart && file.productSpecs?.title)
        .map((file) => {
          const product = this.availableProducts.find(
            (p) => p.title === file.productSpecs.title
          )

          if (!this.hasQuantitySource(product)) return null

          return {
            fileName: file.fileName,
            quantity: this.productQuantity(product, file)
          }
        })
        .filter(Boolean)
    },
    availableProducts() {
      if (this.secure) {
        return this.productOrderStore.productsSecured
      }
      const userLevel = this.rootStore.user.securityLevel
      return this.productOrderStore.products.filter((prod) => {
        const prodLevel = prod.securityLevel ? prod.securityLevel : 0
        return userLevel >= prodLevel
      })
    },
    // Used for auto-select: a product that is unsuitable for the file type is
    // shown greyed out, so it must never be selected automatically.
    // The secured/regular rule is deliberately not applied here: it would leave
    // a single product (e.g. the secured mailing) and auto-assign it to the next
    // file, which is confusing. selectFile() only refuses a blocked product.
    autoSelectableProducts() {
      return this.availableProducts.filter((product) =>
        this.productFileTypeCheck(product, this.selectedFiles)
      )
    },
    fileListLength() {
      return this.fileListStore.fileList.filter(
        (file) =>
          !file.inCart &&
          file.uploadFileType != 'ADDRESS_LIST' &&
          file.uploadFileType != 'ATTACHMENT_FILE'
      ).length
    },
    dropTarget() {
      return 'app'
      //      if(this.rootStore.theme.customFields.printOrder.)
    }
  },
  watch: {
    // A product can be selected (or auto-selected when it is the only one) while the
    // file analysis is still running. Once numberOfLines arrives, the quantity of any
    // file whose product derives it from the analysis has to catch up.
    analysedQuantities: {
      handler(newVal) {
        newVal.forEach(({ fileName, quantity }) => {
          const file = this.fileListStore.fileList.find(
            (f) => f.fileName === fileName
          )

          if (file && file.quantity !== quantity) {
            this.fileListStore.updateProductOrderQuantity([[fileName], quantity])

            if (!file.productSpecs?.hidePrice) {
              this.fileListStore.setProductOrderPrice({
                fileNames: [fileName],
                spec: {},
                hydrate: false
              })
            }
          }
        })
      },
      deep: true
    },
    'rootStore.uploadInProgress': function (newVal, oldVal) {
      if (newVal === false && oldVal === true) {
        if (this.availableProducts.length !== 1) {
          if (this.firstSelectableFile) {
            this.selectFile(this.firstSelectableFile, 'single')
          }
          return
        }

        const product = this.availableProducts[0]
        const isSelectable = (file) =>
          !file.inCart &&
          file.uploadFileType !== 'ADDRESS_LIST' &&
          file.uploadFileType !== 'ATTACHMENT_FILE'
        const hasEmptySpecs = (file) =>
          !file.productSpecs?.title ||
          (file.productSpecs.specs && file.productSpecs.specs.length === 0) ||
          Object.keys(file.productSpecs).length === 0

        const filesToAssign = this.fileListStore.fileList.filter(
          (file) => isSelectable(file) && hasEmptySpecs(file)
        )

        if (
          filesToAssign.length === 0 ||
          !this.productFileTypeCheck(product, filesToAssign)
        ) {
          if (this.firstSelectableFile) {
            this.selectFile(this.firstSelectableFile, 'single')
          }
          return
        }

        const firstFileName = filesToAssign[0].fileName
        this._afterSelectProduct = () => {
          this.selectedFiles = [
            this.fileListStore.fileList.find((f) => f.fileName === firstFileName) || filesToAssign[0]
          ]
        }
        this.selectedFiles = filesToAssign
        this.selectProduct(product)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.drag-glow {
  //  box-shadow: rgb(255 255 255) 0px 0px 9px 5px;
  box-shadow: 0px 0px 25px 9px;
}

#cart {
  display: flex;
  width: 100%;
  flex-direction: column;
}

.file-list {
  width: 100%;
}

#file-specs {
  min-height: 615px;
  width: 100%;
  //  max-width: 594px;
  //  max-width: 736px;

  /*   header {
    padding-left: 20px;
    height: 19px;

    h3 {
      display: inline-block;
    }
  } */

  > :nth-child(2) {
    height: calc(100% - 19px);
  }
}

@media screen and (min-width: 620px) {
  .file-list {
    margin-right: 10px;
  }
}

@media screen and (min-width: 1024px) {
  #cart {
    flex-direction: row;
  }

  .mobile-cart {
    display: none;
  }
}

@media screen and (min-width: 1320px) {
  .file-list {
    max-width: 500px;
  }
}
</style>
